<script lang="ts">
const __DsBlock = {
	type: 'input',
	icon: 'lucide:text-cursor-input',
	name: '$t:blocks.input_uuid.name',
	description: '$t:blocks.input_uuid.description',
	category: 'input',
	tags: ['form-control'],
	fieldBinding: {
		supportedTypes: ({ primitiveType }) => (isPrimitiveTypeUuid(primitiveType) ? ['identifier'] : []),
	},

	options: [
		{
			name: 'modelValue',
			label: '$t:blocks.common.props.value',
			model: true,
		},
		{
			name: 'allowUpdate',
			label: '$t:blocks.input_uuid.props.allow_update',
		},
		{
			name: 'version',
			label: '$t:blocks.input_uuid.props.version',
			type: 'enum',
			values: ['v4', 'v7'],
			nullable: true,
		},
		{
			name: 'placeholder',
			label: '$t:blocks.common.props.placeholder',
		},
		{
			name: 'variant',
			label: '$t:blocks.common.props.variant',
			type: 'enum',
			values: BLOCK_INPUT_VARIANT_OPTIONS,
		},
		{
			name: 'size',
			label: '$t:blocks.common.props.size',
			type: 'enum',
			values: BLOCK_CONTROL_SIZE_OPTIONS,
		},
		{
			name: 'leadingIcon',
			label: '$t:blocks.common.props.leading_icon',
			block: BuiltinBlockIds.InputIcon,
			nullable: true,
		},
		{
			name: 'trailingIcon',
			label: '$t:blocks.common.props.trailing_icon',
			block: BuiltinBlockIds.InputIcon,
			nullable: true,
		},
		{
			name: 'block',
			label: '$t:blocks.common.props.block',
		},
		{
			name: 'loading',
			label: '$t:blocks.common.props.loading',
		},
		{
			name: 'disabled',
			label: '$t:blocks.common.props.disabled',
		},
		{
			name: 'readonly',
			label: '$t:blocks.common.props.readonly',
		},
		{
			name: 'disabledNotice',
			label: '$t:blocks.common.props.disabled_notice',
		},
		{ name: 'modelModifiers', hidden: true },
	],
}
</script>
<script lang="ts" setup>
import type { UuidVersion } from '#layers/base/public';
import type { InputBlockProps } from '#layers/blocks/types/input-block';
import type { InputProps } from '#layers/ui/public';
import { BuiltinBlockIds } from 'virtual:monospace-builtin-block-ids';
import { computed } from 'vue';
import { randomUuid } from '#layers/base/public';
import { defineBlock } from '#layers/block-engine/public';
import { BLOCK_CONTROL_SIZE_OPTIONS, BLOCK_INPUT_VARIANT_OPTIONS } from '#layers/blocks/constants/ui-options';
import { getConfiguredUuidVersion, isPrimitiveTypeUuid } from '#layers/data-model/public';
import { injectFormBlockContext, usePrimitiveFormField } from '#layers/forms/public';
import { useDisabledNotice } from '#layers/ui/public';

interface Props extends InputBlockProps, Pick<
	InputProps<any>,
	'placeholder' | 'variant' | 'size' | 'leadingIcon' | 'trailingIcon' | 'loading' | 'modelModifiers'
> {
	/**
	 * Render the input full width
	 */
	block?: boolean;
	/**
	 * Allow updating of the value.
	 *
	 * @default false
	 */
	allowUpdate?: boolean;
	/**
	 * UUID version generated by the refresh button. Defaults to the bound field's
	 * configured version, or v4 when the field has no UUID default.
	 */
	version?: UuidVersion;
}

const {
	block = true,
	allowUpdate,
	version,
	disabled: disabledProp,
	disabledNotice: disabledNoticeProp,
	size,
	placeholder,
	variant,
	leadingIcon,
	trailingIcon,
	loading,
} = defineProps<Props>();
const model = defineModel<string>();

;

const inputProps = computed(() => ({ placeholder, variant, size, leadingIcon, trailingIcon, loading }));
const buttonProps = computed(() => ({ size }));

const formBlockContext = injectFormBlockContext(null);

const { modelValue, disabled, disabledNotice, field } = usePrimitiveFormField(() => ({ disabled: disabledProp, disabledNotice: disabledNoticeProp }), { model });
const { showDisabledNotice } = useDisabledNotice({ disabled, disabledNotice });
const hasValue = computed(() => !!modelValue.value);
const isLocked = computed(() => !allowUpdate && (hasValue.value && !!formBlockContext && !formBlockContext.isCreate.value));
const inputDisabled = computed(() => disabled.value || isLocked.value);

function generate() {
	modelValue.value = randomUuid(version ?? getConfiguredUuidVersion(field.value?.defaultValue));
}
</script>

<template>
	<DTooltip :delay-duration="0" :disabled="!showDisabledNotice" :text="disabledNotice">
		<!-- `block` widens, it must not grow: the form's control column is a vertical flex box, so
			`flex-1` would stretch the group over a taller row and only the button would follow, leaving
			its edges taller than the input's. -->
		<DFieldGroup :class="{ 'w-full': block }">
			<DInput
				v-bind="inputProps"
				v-model="modelValue"
				:model-modifiers="modelModifiers ?? {}"
				:disabled="inputDisabled"
				:readonly
				:aria-readonly="readonly"
				class="min-w-0 flex-1 font-mono font-medium"
			/>
			<DTooltip v-if="!readonly" :delay-duration="0" :disabled="!inputDisabled" :text="$t('blocks.input_uuid.update_not_allowed')">
				<DButton
					v-bind="buttonProps"
					color="neutral"
					variant="outline"
					icon="lucide:refresh-cw"
					class="ring-default disabled:ring-default!"
					:disabled="inputDisabled"
					@click="generate()"
				>
					{{ $t('blocks.input_uuid.generate') }}
				</DButton>
			</DTooltip>
		</DFieldGroup>
	</DTooltip>
</template>
